import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import { notify } from '../../notifications';

interface ReportSecurityWrapperProps {
  enabled?: boolean;
  protectCopy?: boolean;
  username?: string;
  children: React.ReactNode;
}

/**
 * Комплексная система защиты отчетов (Anti-Copy + Размытие при потере фокуса)
 * Активируется правом «Защита от копирования» (protectCopy).
 * 
 * Включает:
 * 1. Блокировку контекстного меню, копирования, вырезания, печати (@media print) и горячих клавиш Ctrl+C, Ctrl+P, Ctrl+S
 * 2. Автоматическое размытие экрана при потере фокуса окна (сворачивание, переключение окон, внешние ножницы)
 */
export const ReportSecurityWrapper: React.FC<ReportSecurityWrapperProps> = ({
  enabled = false,
  protectCopy,
  children
}) => {
  const isProtected = protectCopy !== undefined ? protectCopy : enabled;

  const [isShieldActive, setIsShieldActive] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Повторные попытки (например, Ctrl+C) схлопываются в одно уведомление со счетчиком ×N
  const showSecurityWarning = useCallback((msg: string) => {
    notify.warning(msg, { duration: 3500 });
  }, []);

  // Синхронная активация экрана блокировки
  const activateShield = useCallback((reason?: string) => {
    if (contentRef.current) {
      contentRef.current.style.filter = 'blur(22px)';
      contentRef.current.style.pointerEvents = 'none';
      contentRef.current.style.userSelect = 'none';
    }
    if (overlayRef.current) {
      overlayRef.current.style.display = 'flex';
    }
    setIsShieldActive(true);
    if (reason) {
      showSecurityWarning(reason);
    }
  }, [showSecurityWarning]);

  // Снятие блокировки при возвращении пользователя в окно
  const deactivateShield = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.style.filter = 'none';
      contentRef.current.style.pointerEvents = 'auto';
      contentRef.current.style.userSelect = isProtected ? 'none' : 'auto';
    }
    if (overlayRef.current) {
      overlayRef.current.style.display = 'none';
    }
    setIsShieldActive(false);
  }, [isProtected]);


  useEffect(() => {
    if (!isProtected) return;

    // 1. Блокировка контекстного меню
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      showSecurityWarning('Контекстное меню и копирование заблокированы политикой безопасности');
    };

    // 2. Блокировка событий копирования и вырезания
    const handleCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      showSecurityWarning('Копирование данных запрещено политикой безопасности');
    };

    const handleCut = (e: ClipboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      showSecurityWarning('Вырезание данных запрещено');
    };

    // 3. Блокировка клавиатурных комбинаций
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCtrlOrMeta = e.ctrlKey || e.metaKey;

      if (isCtrlOrMeta && (e.key === 'c' || e.key === 'C' || e.key === 'с' || e.key === 'С')) {
        e.preventDefault();
        e.stopPropagation();
        showSecurityWarning('Копирование сочетанием клавиш запрещено');
        return;
      }

      if (isCtrlOrMeta && (e.key === 'p' || e.key === 'P' || e.key === 'з' || e.key === 'З')) {
        e.preventDefault();
        e.stopPropagation();
        showSecurityWarning('Печать отчета запрещена');
        return;
      }

      if (isCtrlOrMeta && (e.key === 's' || e.key === 'S' || e.key === 'ы' || e.key === 'Ы')) {
        if (!e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          showSecurityWarning('Сохранение страницы запрещено');
          return;
        }
      }

      if (isCtrlOrMeta && (e.key === 'u' || e.key === 'U' || e.key === 'г' || e.key === 'Г')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Блокировка клавиши PrintScreen
      if (e.key === 'PrintScreen' || e.code === 'PrintScreen') {
        e.preventDefault();
        e.stopPropagation();
        activateShield('Снимок экрана заблокирован политикой безопасности');
        try {
          navigator.clipboard.writeText('');
        } catch { }
        return;
      }
    };

    // 4. Надежное размытие при потере фокуса ОКНА (а не отдельных инпутов/селекторов)
    let blurTimeout: NodeJS.Timeout | null = null;

    const handleWindowBlur = () => {
      // 150мс задержка отсекает внутренние события смены фокуса между элементами страницы
      if (blurTimeout) clearTimeout(blurTimeout);
      blurTimeout = setTimeout(() => {
        if (!document.hasFocus() || document.hidden) {
          activateShield();
        }
      }, 150);
    };

    const handleWindowFocus = () => {
      if (blurTimeout) {
        clearTimeout(blurTimeout);
        blurTimeout = null;
      }
      deactivateShield();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        activateShield();
      } else {
        deactivateShield();
      }
    };

    window.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('copy', handleCopy, true);
    document.addEventListener('cut', handleCut, true);
    window.addEventListener('keydown', handleKeyDown, true);

    // Слушатели окна без capture-фазы (не ловят blur дочерних инпутов)
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Если страница загружена в скрытой вкладке
    if (document.hidden) {
      activateShield();
    }

    return () => {
      if (blurTimeout) clearTimeout(blurTimeout);
      window.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('copy', handleCopy, true);
      document.removeEventListener('cut', handleCut, true);
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      deactivateShield();
    };
  }, [isProtected, activateShield, deactivateShield, showSecurityWarning]);

  if (!isProtected) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        // Защита от печати через CSS Media Query
        '@media print': {
          display: 'none !important'
        }
      }}
    >
      {/* 1. Контент отчета с эффектом размытия при потере фокуса */}
      <Box
        ref={contentRef}
        sx={{
          filter: isShieldActive ? 'blur(22px)' : 'none',
          transition: 'filter 0.15s ease',
          pointerEvents: isShieldActive ? 'none' : 'auto'
        }}
      >
        {children}
      </Box>

      {/* 3. Оверлей блокировки экрана при потере фокуса */}
      <Box
        ref={overlayRef}
        onClick={deactivateShield}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: isShieldActive ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(248, 250, 252, 0.92)',
          backdropFilter: 'blur(14px)',
          zIndex: 10,
          borderRadius: '8px',
          p: 3,
          textAlign: 'center',
          cursor: 'pointer'
        }}
      >
        <Box
          sx={{
            p: 3,
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
            maxWidth: 440,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.75
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          {/* <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5, fontSize: '1.05rem' }}>
            Конфиденциальные данные защищены
          </Typography> */}
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem', mb: 2.25, lineHeight: 1.4 }}>
            Окно отчета не активно. Нажмите кнопку ниже или кликните в окно, чтобы продолжить работу.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              deactivateShield();
            }}
            sx={{
              backgroundColor: '#2563eb',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              borderRadius: '8px',
              px: 2.5,
              py: 0.8,
              '&:hover': {
                backgroundColor: '#1d4ed8'
              }
            }}
          >
            Разблокировать просмотр
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
